import React, { useEffect, useState } from 'react';
import MyApiClient from '../../../api-client/MyApiClient';

/*
 * Controllo dei conflitti di orario, uguale in tutta l'app (comportamento delle squadre, booking/courses/occorrenza.js):
 * per il giorno e l'orario scelti chiede al calendario gli eventi dell'impianto e del maestro, esclude l'evento
 * che si sta modificando e segnala quelli che si sovrappongono, con titolo e orario. Con un conflitto non si salva.
 * Il backend ripete comunque il controllo al salvataggio.
 *
 *   const verifica = useConflittiOrario({ data: '2026-10-14', inizio: '18:00', fine: '19:00', impiantoId, staffId, escludiId });
 *   <EsitoConflitto risultato={verifica.impianto} inCorso={verifica.inCorso} nome="Impianto" />
 *   <Button disabled={verifica.inCorso || verifica.conflitto}>Salva</Button>
 *
 * `data` è YYYY-MM-DD; `inizio` e `fine` sono HH:MM (oppure `inizioIso` e `fineIso` già pronti).
 */
const api = new MyApiClient();

const getToken = () => {
    try {
        return JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
    } catch (e) {
        return null;
    }
};

const inputsToIso = (date, time) => {
    if (!date || !time) return null;
    const d = new Date(`${date}T${time}:00`);
    return isNaN(d) ? null : d.toISOString();
};

export const oraBreve = (iso) => new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

export const siSovrappone = (evento, inizio, fine) =>
    new Date(evento.dataInizio) < new Date(fine) && new Date(evento.dataFine) > new Date(inizio);

// Eventi del calendario che occupano l'impianto o il maestro nell'orario dato
export const eventiOccupati = async (token, { data, inizioIso, fineIso, filtro, escludiId }) => {
    // l'elenco eventi vuole data e ora (come la pagina del calendario), la giornata intera
    const res = await api.getCalendarioEventi(token, `${data}T00:00:00`, `${data}T23:59:59`, filtro);
    if (!res || res.error) return { errore: res?.error || 'Verifica non riuscita' };
    return { occupati: (res.eventi || []).filter((e) => e.id !== escludiId && siSovrappone(e, inizioIso, fineIso)) };
};

const VUOTO = { inCorso: false, impianto: null, maestro: null, conflitto: false, orariValidi: false };

export const useConflittiOrario = ({ data, inizio, fine, inizioIso, fineIso, impiantoId, staffId, escludiId, attivo = true }) => {
    const [esito, setEsito] = useState(VUOTO);
    const iso1 = inizioIso || inputsToIso(data, inizio);
    const iso2 = fineIso || inputsToIso(data, fine);
    const orariValidi = !!(iso1 && iso2 && new Date(iso2) > new Date(iso1));

    useEffect(() => {
        const token = getToken();
        if (!attivo || !token || !data || !orariValidi) {
            setEsito({ ...VUOTO, orariValidi });
            return undefined;
        }
        let annullato = false;
        setEsito((v) => ({ ...v, inCorso: true, orariValidi }));
        const timer = setTimeout(async () => {
            const [imp, mae] = await Promise.all([
                impiantoId ? eventiOccupati(token, { data, inizioIso: iso1, fineIso: iso2, filtro: { impianti: impiantoId }, escludiId }) : Promise.resolve(null),
                staffId ? eventiOccupati(token, { data, inizioIso: iso1, fineIso: iso2, filtro: { maestri: staffId }, escludiId }) : Promise.resolve(null),
            ]);
            if (annullato) return;
            const conflitto = (imp?.occupati?.length || 0) > 0 || (mae?.occupati?.length || 0) > 0;
            setEsito({ inCorso: false, impianto: imp, maestro: mae, conflitto, orariValidi });
        }, 400);
        return () => {
            annullato = true;
            clearTimeout(timer);
        };
    }, [attivo, data, iso1, iso2, orariValidi, impiantoId, staffId, escludiId]);

    return esito;
};

// Esito sotto il campo: chip salmone «Impianto: non disponibile» con sotto la frase per ogni evento che occupa;
// la chip verde «nessun altro impegno» c'è solo se non ci sono conflitti (`nascondiOk` la toglie quando un'altra risorsa è in conflitto)
export const EsitoConflitto = ({ risultato, inCorso, nome, nascondiOk = false }) => {
    if (inCorso) return <span className="sid-check sid-check--wait">Verifica in corso…</span>;
    if (!risultato) return null;
    if (risultato.errore) return <span className="sid-check sid-check--wait">{risultato.errore}</span>;
    if (risultato.occupati.length === 0) {
        return nascondiOk ? null : <span className="sid-check sid-check--ok">{nome}: nessun altro impegno in questo orario</span>;
    }
    return (
        <>
            <span className="sid-chip-conflitto">{nome}: non disponibile</span>
            {risultato.occupati.map((e) => (
                <div key={e.id} className="mt-2" style={{ fontSize: 14, color: '#1A1A1A' }}>
                    {nome} occupato: {e.titolo} dalle {oraBreve(e.dataInizio)} alle {oraBreve(e.dataFine)}
                </div>
            ))}
        </>
    );
};

export default useConflittiOrario;
